import * as yup from 'yup';

import { USA, usaStates } from '../../../../shared/constants';
import {formUtils, regex} from '../../../../shared/utils';

const { field, toArrayOfOptions, sectionTitle } = formUtils;

export const initialValues = (forceName) => {
  const propName = forceName ? `${forceName}_` : '';
  return {
    [`${propName}country`]: USA
  }
};


const formSchema = (options, t, fieldsValues, forceName = '') => {
  const values = {
    ...initialValues(forceName),
    ...fieldsValues
  };
  const propName = forceName ? `${forceName}_` : '';
  const { country } = values || {};
  const isCountryUSA = country === USA;

  return [
    sectionTitle('addressSectionTitle', t, 24, { marginTop: 0 }),
    [
      {
        span: 12,
        columns: [
          field('input', `${propName}postalCode`, t, values, { required: true })
        ]
      },
      {
        span: 12,
        columns: [
          field('select', `${propName}country`, t, values, {
            required: true,
            showSearch: true,
            options: options.countries,
            defaultValue: USA
          })
        ]
      }
    ],
    [
      {
        span: 12,
        columns: isCountryUSA
          ? [
              field('select', `${propName}usaState`, t, values, {
                required: true,
                options: usaStates
              })
            ]
          : [
              field('input', `${propName}state`, t, values, {
                required: isCountryUSA
              })
            ]
      },
      {
        span: 12,
        columns: [
          field('input', `${propName}city`, t, values, { required: true })
        ]
      }
    ],
    [
      {
        span: 24,
        columns: [
          field('input', `${propName}billingAddress`, t, values, {
            required: true
          })
        ]
      }
    ],
    [
      {
        span: 24,
        columns: [field('input', `${propName}poNumber`, t, values)]
      }
    ],
    [
      {
        span: 12,
        columns: [
          field('select', `${propName}billingFrequency`, t, values, {
            required: true,
            preventDirt: true,
            options: toArrayOfOptions(options.billingFrequency)
          })
        ]
      }
    ],
    sectionTitle('contactSectionTitle', t),
    [
      {
        span: 12,
        columns: [
          field('input', `${propName}name`, t, values, {
            required: true,
            placeholder: t('name.placeholder')
          })
        ]
      },
      {
        span: 12,
        columns: [
          field('input', `${propName}email`, t, values, {
            required: true,
            placeholder: t('email.placeholder')
          })
        ]
      }
    ],[
      {
        span: 12,
        columns: [
          field('input', `${propName}phoneNumber`, t, values, {
            required: true,
            tabIndex: forceName ? 7 : 4,
            placeholder: t('phoneNumber.placeholder'),
            showCountrySelector: true,
            defaultValue: "+1",
            mask: 'phone'
          })
        ]
      },
    ]
  ];
};

const invoiceSchema = (
  options,
  t,
  values,
  instancesDescription,
  { hasIntegrationDriver, hasDhis2SupportPlan }
) => {
  let integrationDriverChecked = false;
  let dhis2SupportChecked = false;
  if (values['groupSeparateInvoice']) {
    integrationDriverChecked =
      values['groupSeparateInvoice']['integrationDriver'] || false;
    dhis2SupportChecked =
      values['groupSeparateInvoice']['dhis2SupportPlan'] || false;
  }
  const instances = instancesDescription.map(({ instanceId, description }) => {
    const name = `instance_${instanceId}`;
    return {
      name,
      label: description,
      checked: values['groupSeparateInvoice']
        ? values['groupSeparateInvoice'][name]
        : false
    };
  });

  const group = [...instances];

  if (hasIntegrationDriver)
    group.push({
      name: 'integrationDriver',
      label: t('integrationDriver.label'),
      checked: integrationDriverChecked
    });

  if (hasDhis2SupportPlan)
    group.push({
      name: 'dhis2SupportPlan',
      label: t('dhis2Support.label'),
      checked: dhis2SupportChecked
    });

  let groupSeparateInvoiceValue = values
    ? {
        ...values['groupSeparateInvoice']
      }
    : {};

  if (
    hasIntegrationDriver &&
    values &&
    values['groupSeparateInvoice'] &&
    values['groupSeparateInvoice'].integrationDriver
  )
    groupSeparateInvoiceValue['integrationDriver'] = true;
  else delete groupSeparateInvoiceValue['integrationDriver'];

  if (
    hasDhis2SupportPlan &&
    values &&
    values['groupSeparateInvoice'] &&
    values['groupSeparateInvoice'].dhis2SupportPlan
  )
    groupSeparateInvoiceValue['dhis2SupportPlan'] = true;
  else delete groupSeparateInvoiceValue['dhis2SupportPlan'];

  if (!Object.keys(groupSeparateInvoiceValue).length)
    groupSeparateInvoiceValue = undefined;

  return [
    sectionTitle('selectItemsSectionTitle', t),
    [
      {
        span: 24,
        columns: [
          field('checkboxGroup', 'groupSeparateInvoice', t, values, {
            value: groupSeparateInvoiceValue,
            group
          })
        ]
      }
    ],
    [
      {
        span: 24,
        columns: [
          field('checkbox', 'differentBillingActive', t, values, {
            title: true,
            style: { marginTop: 22, marginBottom: 12 }
          })
        ]
      }
    ],
    ...(values && values.differentBillingActive
      ? formSchema(options, t, values, 'differentBilling')
      : [])
  ];
};

const separateInvoiceValidationSchema = (t, values, forceName) => {
  return !values['differentBillingActive']
    ? yup.object().shape({
        [`groupSeparateInvoice`]: yup
          .string()
          .required(t(`groupSeparateInvoice.error_required`))
      })
    : validationSchema(t, values, forceName, {
        [`groupSeparateInvoice`]: yup
          .string()
          .required(t(`groupSeparateInvoice.error_required`))
      });
};

const validationSchema = (t, values, forceName = '', extraValidation = {}, data) => {
  const propName = forceName ? `${forceName}_` : '';
  const isCountryUSA = values ? values['country'] === USA : false;

  const states = isCountryUSA
    ? {
        [`${propName}usaState`]: yup
          .string()
          .required(t(`usaState.error_required`))
      }
    : {};
  return yup.object().shape({
    ...extraValidation,
    ...states,
    [`${propName}name`]: yup.string().required(t(`name.error_required`)),
    [`${propName}email`]: yup
      .string()
      .email(t('email.error_invalid'))
      .required(t(`email.error_required`)),
    [`${propName}billingAddress`]: yup
      .string()
      .required(t('billingAddress.error_required')),
    [`${propName}city`]: yup.string().required(t('city.error_required')),
    [`${propName}postalCode`]: yup
      .string()
      .required(t('postalCode.error_required')),
    [`${propName}country`]: yup.string().required(t('country.error_required')),
    [`${propName}billingFrequency`]: yup
      .string()
      .oneOf(Object.keys(data.options.billingFrequency), t('billingFrequency.error_required'))
      .required(t('billingFrequency.error_required')),
    [`${propName}phoneNumber`]: yup
      .string()
      .matches(regex.phoneCountryCode, t(`phoneNumber.error_required`))
      .matches(regex.phoneMinimumLength, t(`phoneNumber.error_invalid`))
      .required(t('phoneNumber.error_required'))
  });
};

export {
  formSchema,
  validationSchema,
  invoiceSchema,
  separateInvoiceValidationSchema
};
